import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ChevronRight, Printer, Check, Package, StickyNote, Pencil, X,
  Camera, Trash2, AlertTriangle,
} from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { code128Geometry } from '../../utils/code128';
import {
  hardwareApi, money,
  type DeviceModelRow, type DeviceUnitRow, type DeviceUnitReceipt,
} from '../../services/hardwareApi';
import { landedCostCents as rawLandedCostCents } from '@platform/shared-types';
import type { DeltaDocument } from '@platform/shared-types';
import { unitBadgeLabel, worstBadgeSeverity } from './unitBadgeSafe';
import UnitNotesEditor, { EMPTY_DELTA, deltaToPlainText, type UnitNotesValue } from './UnitNotesEditor';

/**
 * ONE RECEIVED UNIT, AS A CARD IN THE GRN'S SUBTREE.
 *
 * The old row was a tick, an RTA id and a Label button. That is enough to prove
 * the scan registered and nothing else: it cannot tell you which of five units
 * you scanned before lunch, cannot show you the thing you are holding, and has
 * nowhere to record that this one arrived scratched.
 *
 * Collapsed it carries the model's photo, the tracking id, a scannable barcode
 * and the timestamp. Expanded it carries the rest, plus the two fields that are
 * genuinely per-unit: the note and the cost.
 */

/**
 * The tracking id as a real CODE128-B symbol.
 *
 * Drawn from `code128Geometry` — the SAME encoder the Rollo label printer uses,
 * never a second implementation. A barcode on screen that disagreed with the
 * one on the printed label would be worse than no barcode at all, and the
 * geometry is in module units so one encoder serves a 20px strip and a 54px
 * block equally crisply.
 */
const Barcode: React.FC<{ value: string; width: number; height: number }> = ({ value, width, height }) => {
  const { bars, modules } = useMemo(() => code128Geometry(value), [value]);
  if (!modules) return null;
  return (
    <svg
      viewBox={`0 0 ${modules} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Barcode ${value}`}
      style={{ display: 'block' }}
    >
      {bars.map((b, i) => (
        <rect key={i} x={b.x} y={0} width={b.width} height={height} fill="#1C1917" />
      ))}
    </svg>
  );
};

/**
 * Guarded like the badge helpers: a stale pre-bundle would make this
 * `undefined` and every cost on the screen would throw at render. Summing the
 * three lines is the same arithmetic `landedCostCents` performs, so the
 * fallback is correct rather than merely safe — it just cannot be kept in step
 * automatically if the real one ever changes.
 */
const landedCostCents: typeof rawLandedCostCents = typeof rawLandedCostCents === 'function'
  ? rawLandedCostCents
  : ((e: any) => (e?.unitCostCents || 0) + (e?.inboundShippingCents || 0) + (e?.dutyCents || 0)) as any;

/** Common arrivals worth one tap rather than a sentence. */
const QUICK_NOTES = ['Scratched', 'Box damaged', 'Missing accessory', 'Hold for QA'];

export interface CostBreakdown {
  unitCostCents: number;
  inboundShippingCents: number;
  dutyCents: number;
}

/** The three editable lines, in the device Cost card's order. */
const COST_ROWS: Array<[keyof CostBreakdown, string]> = [
  ['unitCostCents', 'Unit cost'],
  ['inboundShippingCents', 'Inbound shipping'],
  ['dutyCents', 'Duty / customs'],
];

const centsToInput = (c: number) => (c / 100).toFixed(2);
const inputToCents = (v: string) => Math.max(0, Math.round((parseFloat(v.replace(/[^\d.]/g, '')) || 0) * 100));

/**
 * WHERE A UNIT'S COST STARTS FROM.
 *
 * A breakdown the operator already entered wins. Failing that, the DEVICE's own
 * economics — the figures maintained once on the model — so receiving inherits
 * what the catalogue already knows instead of asking for it again. A unit that
 * only ever had a flat `costCents` puts it on the unit-cost line, which is where
 * a single number came from in the first place.
 */
export function initialBreakdown(unit: DeviceUnitRow, model?: DeviceModelRow): CostBreakdown {
  if (unit.costBreakdown) return { ...unit.costBreakdown };
  const e = model?.economics;
  if (e) {
    return {
      unitCostCents: e.unitCostCents ?? 0,
      inboundShippingCents: e.inboundShippingCents ?? 0,
      dutyCents: e.dutyCents ?? 0,
    };
  }
  return { unitCostCents: unit.costCents ?? 0, inboundShippingCents: 0, dutyCents: 0 };
}

/** 10 MB, the same ceiling `PairReceipts` puts on a receipt photo. */
const MAX_LABEL_BYTES = 10 * 1024 * 1024;

/**
 * THE PHOTOGRAPH OF THE DEVICE'S OWN STICKER.
 *
 * WHY A PHOTO AND NOT FIELDS. A terminal's label carries far more than its
 * serial: the FCC ID, a WiFi MAC, a Bluetooth MAC, and on a payment device
 * usually two or three more numbers besides. Which of them will be wanted is
 * not known at receiving time — a certification query a year later wants the
 * FCC ID, a network fault wants the MACs — and a form with four boxes on it
 * silently discards the fifth number and mistypes the other four. The photo
 * keeps the label whole and costs the operator one tap.
 *
 * The serial stays a typed field ANYWAY, because it is the one value that has
 * to be searchable, and because it is the one the scanner can read directly.
 *
 * WHY THIS USES `/photos/presign` AND NOT uploads-service. A unit is
 * photographed at RECEIVING, when it belongs to no tenant yet, and
 * uploads-service is gated by the tenant Cognito pool — it 401s for
 * super-admin. hardware-service's own presign is the admin-pool door, and it
 * returns a durable `fileUrl`, which is why a label receipt stores a URL where
 * a test/refund receipt stores only a key.
 *
 * Uploads are SEQUENTIAL, matching `PairReceipts`: each add is conditional on
 * the unit's `updatedAt`, so parallel writes would have one lose with a 409
 * and quietly drop a photo the operator believes they took.
 */
const LabelPhotos: React.FC<{
  unit: DeviceUnitRow;
  /** Serialized units must have one; bulk stock has no sticker to photograph. */
  required: boolean;
  onUnitChanged?: (unit: DeviceUnitRow) => void;
}> = ({ unit, required, onUnitChanged }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const photos = (unit.receipts || []).filter((r) => r.kind === 'label');
  const missing = required && photos.length === 0;

  const upload = useCallback(async (files: FileList | null) => {
    const chosen = Array.from(files || []);
    if (chosen.length === 0) return;

    const usable = chosen.filter((f) => f.type.startsWith('image/') && f.size <= MAX_LABEL_BYTES);
    const rejected = chosen.filter((f) => !usable.includes(f));
    if (rejected.length) {
      // NAMED, not counted — "1 of 3 failed" is useless when the missing one
      // is the label you cannot re-photograph because the box has shipped.
      setError(`${rejected.map((f) => f.name).join(', ')} — needs to be a photo under 10 MB.`);
    }
    if (usable.length === 0) return;

    setBusy(true);
    setProgress({ done: 0, total: usable.length });
    let latest = unit;
    const failed: string[] = [];

    for (const [i, file] of usable.entries()) {
      try {
        const signed = await hardwareApi.presignPhoto({
          modelId: unit.modelId,
          filename: `label-${unit.trackingId || unit.unitId}-${Date.now()}.${
            (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg'
          }`,
          contentType: file.type,
          bytes: file.size,
        });
        const put = await fetch(signed.uploadUrl, {
          method: 'PUT', headers: { 'Content-Type': file.type }, body: file,
        });
        if (!put.ok) throw new Error(`the upload was refused (${put.status})`);

        const res = await hardwareApi.addReceipt(latest.unitId, {
          kind: 'label',
          key: signed.key,
          url: signed.fileUrl,
          name: file.name.slice(0, 200),
          contentType: file.type,
        });
        if (res?.unit) { latest = res.unit; onUnitChanged?.(res.unit); }
      } catch (e: any) {
        failed.push(`${file.name} — ${e?.serverMessage || e?.message || 'failed'}`);
      }
      setProgress({ done: i + 1, total: usable.length });
    }

    setBusy(false);
    setProgress(null);
    if (inputRef.current) inputRef.current.value = '';
    if (failed.length) setError(failed.join(' · '));
  }, [unit, onUnitChanged]);

  const remove = async (photo: DeviceUnitReceipt) => {
    setRemoving(photo.receiptId);
    setError(null);
    try {
      const res = await hardwareApi.removeReceipt(unit.unitId, photo.receiptId);
      if (res?.unit) onUnitChanged?.(res.unit);
    } catch (e: any) {
      setError(e?.serverMessage || e?.message || 'Could not remove that photo.');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className={`rounded-lg border p-2.5 ${
      missing ? 'border-[#DC143C] bg-[#FFF1F3]' : 'border-stone-200 bg-white'
    }`}>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
          Label photo
        </span>
        {required && (
          <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
            missing ? 'bg-[#DC143C] text-white' : 'bg-emerald-100 text-emerald-700'
          }`}>
            {missing ? 'Required' : 'On file'}
          </span>
        )}
      </div>

      {missing && (
        <p className="mb-2 flex items-start gap-1.5 text-[11px] leading-snug text-[#9F1239]">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          Photograph the sticker before this unit leaves receiving — it is the only record of
          the FCC ID, the MACs and any serial other than the one typed above.
        </p>
      )}

      {photos.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {photos.map((p) => (
            <div key={p.receiptId} className="group relative">
              <a
                href={p.url || undefined}
                target="_blank"
                rel="noreferrer"
                className="block h-16 w-16 overflow-hidden rounded-lg border border-stone-200 bg-stone-50"
                title={p.name || 'Label photo'}
              >
                {/* `url` is absent only on a row written before it existed;
                    the tile still renders, it just cannot be previewed. */}
                {p.url
                  ? <img src={p.url} alt={p.name || 'Device label'} className="h-full w-full object-cover" />
                  : <span className="flex h-full w-full items-center justify-center text-stone-300"><Camera size={18} /></span>}
              </a>
              <button
                type="button"
                onClick={() => remove(p)}
                disabled={removing === p.receiptId}
                aria-label={`Remove ${p.name || 'label photo'}`}
                className="absolute -right-1.5 -top-1.5 rounded-full border border-stone-300 bg-white p-1 text-stone-500 shadow-sm hover:border-[#DC143C] hover:text-[#DC143C] disabled:opacity-40"
              >
                {removing === p.receiptId
                  ? <RubyGemMark size={10} drawing color="currentColor" />
                  : <Trash2 size={10} />}
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        /* `environment` opens the rear camera straight onto the sticker on a
           phone or tablet, which is what receiving is actually done on. */
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => upload(e.target.files)}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={`inline-flex min-h-[36px] w-full items-center justify-center gap-1.5 rounded-lg border text-[12px] font-semibold disabled:opacity-40 ${
          missing
            ? 'border-[#DC143C] bg-[#DC143C] text-white'
            : 'border-stone-300 bg-white text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C]'
        }`}
      >
        {busy
          ? <><RubyGemMark size={12} drawing color="currentColor" /> {progress ? `${progress.done}/${progress.total}` : 'Uploading'}</>
          : <><Camera size={12} /> {photos.length ? 'Add another' : 'Photograph label'}</>}
      </button>

      {error && <p className="mt-1.5 text-[11px] font-medium text-[#DC143C]">{error}</p>}
    </div>
  );
};

/**
 * THE BOUGHT LABELS STUCK ON THIS DEVICE.
 *
 * Aliases, not identity. The RTA barcode above is ours and authoritative;
 * these are the adhesive codes that come off a roll already printed, so a
 * device can be scanned before our own barcode exists on anything durable —
 * and afterwards too, since a label wears out and the material may change
 * while the system id never does.
 *
 * MANY PER DEVICE on purpose. One on the box, one on the base, a fresh one
 * over a peeling one: all of them reach the same unit.
 *
 * The 409 is worth showing verbatim rather than as "failed". A code already
 * on another device is a real-world mistake with a real-world fix — peel it
 * off that one first — and the service refuses it precisely so two machines
 * can never answer to one number.
 */
const ExternalBarcodes: React.FC<{
  unit: DeviceUnitRow;
  onUnitChanged?: (unit: DeviceUnitRow) => void;
}> = ({ unit, onUnitChanged }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const codes = unit.externalBarcodes || [];

  const link = async () => {
    const value = code.trim();
    if (!value || busy) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const res = await hardwareApi.linkBarcode(unit.unitId, value);
      if (res?.unit) onUnitChanged?.(res.unit);
      setNote(res?.alreadyLinked
        ? `${value} was already on this device.`
        : `${value} now scans to this device.`);
      setCode('');
      // Straight back to the field: labelling a box is a run of scans, not one.
      inputRef.current?.focus();
    } catch (e: any) {
      setError(e?.serverMessage || e?.message || 'Could not link that barcode.');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (value: string) => {
    setRemoving(value); setError(null); setNote(null);
    try {
      const res = await hardwareApi.unlinkBarcode(unit.unitId, value);
      if (res?.unit) onUnitChanged?.(res.unit);
    } catch (e: any) {
      setError(e?.serverMessage || e?.message || 'Could not remove that barcode.');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-2.5">
      <div className="mb-1.5 text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
        Extra barcodes
        <span className="ml-1 font-normal normal-case tracking-normal text-stone-400">
          · bought labels that scan to this device
        </span>
      </div>

      {codes.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {codes.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-stone-50 py-0.5 pl-2 pr-1 font-mono text-[11px] text-stone-700">
              {c}
              <button
                type="button"
                onClick={() => unlink(c)}
                disabled={removing === c}
                aria-label={`Remove barcode ${c}`}
                className="rounded-full p-0.5 text-stone-400 hover:text-[#DC143C] disabled:opacity-40"
              >
                {removing === c ? <RubyGemMark size={10} drawing color="currentColor" /> : <X size={10} />}
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          /* A scanner is a keyboard that ends with Enter, so Enter links
             rather than submitting the page. */
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); link(); } }}
          placeholder="Scan a label to add it"
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 font-mono text-[12px] text-stone-800 outline-none focus:border-[#DC143C]"
        />
        <button
          type="button"
          onClick={link}
          disabled={busy || !code.trim()}
          className="shrink-0 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[11.5px] font-semibold text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C] disabled:opacity-40"
        >
          {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : 'Add'}
        </button>
      </div>

      {error && <p className="mt-1.5 text-[11px] font-medium text-[#DC143C]">{error}</p>}
      {note && !error && <p className="mt-1.5 text-[11px] text-emerald-700">{note}</p>}
      {codes.length === 0 && !error && !note && (
        <p className="mt-1 text-[10.5px] leading-snug text-stone-500">
          Optional. The {' '}
          <span className="font-mono">RTA</span>
          {' '} barcode above always works — these are extra labels for the same device.
        </p>
      )}
    </div>
  );
};

const Fact: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
  <div className="min-w-0">
    <div className="text-[9.5px] font-bold uppercase tracking-wide text-stone-400">{label}</div>
    <div className={`truncate text-[12.5px] text-stone-700 ${mono ? 'font-mono text-[11.5px]' : ''}`}>{children}</div>
  </div>
);

const ReceivedUnitCard: React.FC<{
  unit: DeviceUnitRow;
  model?: DeviceModelRow;
  grnNumber: string;
  vendorName?: string;
  /** Highlights a unit that has just landed from a scan. */
  highlight?: boolean;
  defaultOpen?: boolean;
  onPrintLabel: (unitId: string) => void;
  onInspectModel?: (modelId: string) => void;
  /** Persists the serial, the note and the landed cost with its breakdown. */
  onSave: (
    unitId: string,
    patch: {
      notes: string; notesRich: DeltaDocument; badges: string[];
      costCents: number; costBreakdown: CostBreakdown;
      serialNumber?: string;
    },
  ) => Promise<void>;
  /**
   * A label photo is written by its OWN endpoint, not by `onSave`, so the card
   * hands the updated unit back rather than waiting for a page reload — the
   * "Required" badge has to clear the moment the photo lands.
   */
  onUnitChanged?: (unit: DeviceUnitRow) => void;
}> = ({
  unit, model, grnNumber, vendorName, highlight, defaultOpen,
  onPrintLabel, onInspectModel, onSave, onUnitChanged,
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [noteValue, setNoteValue] = useState<UnitNotesValue>(() => ({
    notes: unit.notes || '',
    // A unit written before rich notes existed still has its plain text; seed
    // the editor from it rather than opening blank and inviting someone to
    // retype what is already there.
    notesRich: (unit.notesRich as DeltaDocument)
      || ({ ops: [{ insert: `${unit.notes || ''}\n` }] } as DeltaDocument),
    badges: unit.badges || [],
  }));
  const [serial, setSerial] = useState(unit.serialNumber || '');
  const [breakdown, setBreakdown] = useState<CostBreakdown>(() => initialBreakdown(unit, model));
  const [costOpen, setCostOpen] = useState(false);
  // The popup edits a DRAFT, so Cancel genuinely discards and the row keeps
  // showing the figure that is actually saved.
  const [draft, setDraft] = useState<CostBreakdown>(breakdown);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const label = unit.trackingId || unit.unitId;
  const at = unit.createdAt ? new Date(unit.createdAt) : null;

  // `landedCostCents` from shared-types, never re-implemented here: the device
  // editor computes the same figure with the same function, and two versions of
  // "what does this cost" that could drift is precisely the bug worth avoiding.
  const costCents = landedCostCents({ ...breakdown, defaultOutboundShippingCents: 0 });
  const savedCost = unit.costCents ?? 0;
  const badgesChanged = JSON.stringify([...noteValue.badges].sort())
    !== JSON.stringify([...(unit.badges || [])].sort());
  const dirty = noteValue.notes.trim() !== (unit.notes || '').trim()
    || badgesChanged
    || serial.trim() !== (unit.serialNumber || '').trim()
    || costCents !== savedCost;

  /* Bulk stock is counted, not identified: a carton of cables has no serial
     and no sticker, so neither is asked of it. `trackingMode` is absent on
     every model predating that field and resolves to 'serialized' — the same
     default the schema takes — so an older model is asked, which is the safe
     direction to be wrong in. */
  const isSerialized = model?.trackingMode !== 'quantity';

  /*
    THE UNIT AS THE PHOTO ENDPOINT LAST RETURNED IT.

    `receipts` is server-owned and written by its own endpoint, so the `unit`
    prop is stale from the moment a photo lands until the parent's reload
    comes back. Holding the returned row here means the thumbnail and the
    "Required" badge both settle on the tap rather than a round trip later —
    and once the reload does arrive it carries the same photo, so the two
    never disagree.
  */
  const [liveUnit, setLiveUnit] = useState<DeviceUnitRow | null>(null);
  const withPhotos = liveUnit && liveUnit.unitId === unit.unitId ? liveUnit : unit;
  const needsLabelPhoto = isSerialized
    && !(withPhotos.receipts || []).some((r) => r.kind === 'label');

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await onSave(unit.unitId, {
        notes: noteValue.notes.trim(),
        notesRich: noteValue.notesRich,
        badges: noteValue.badges,
        costCents,
        costBreakdown: breakdown,
        /* Only sent when the model is serialized, and `undefined` rather than
           '' when cleared: the upsert MERGES, so an empty string would write a
           blank serial over a good one if the field were ever rendered empty
           by a stale load. */
        serialNumber: isSerialized ? (serial.trim() || undefined) : undefined,
      });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (e: any) {
      setError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`overflow-hidden rounded-xl border bg-white ${
      highlight ? 'border-[#DC143C] bg-[#FFF1F3]' : 'border-stone-200'
    }`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="grid w-full grid-cols-[40px_1fr_auto_16px] items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50"
      >
        <ShimmerImage
          src={model?.media?.heroImageUrl}
          alt={model?.modelName || 'Device'}
          width={40}
          height={40}
          objectFit="contain"
          variant="light"
          className="rounded-lg border border-stone-200 bg-stone-50"
          fallbackBgColor="bg-stone-50"
          fallbackIcon={<Package size={17} className="text-stone-300" />}
        />
        <span className="min-w-0">
          <span className="block font-mono text-[13.5px] font-semibold text-stone-900">{label}</span>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            <Barcode value={label} width={150} height={20} />
            {/* Prefixed, so the two numbers on this row are never read as one.
                The barcode beside it is ours; this is the maker's. */}
            {unit.serialNumber && (
              <span className="font-mono text-[11px] text-stone-500">
                <span className="font-sans text-[9.5px] font-bold uppercase tracking-wide text-stone-400">S/N </span>
                {unit.serialNumber}
              </span>
            )}
            {/* VISIBLE WITHOUT EXPANDING. A GRN can hold thirty cards; a
                warning that only shows once you open one is a warning nobody
                sees on the twenty-nine they did not open. */}
            {needsLabelPhoto && (
              <span className="inline-flex items-center gap-1 rounded bg-[#DC143C] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                <Camera size={9} /> Label photo
              </span>
            )}
            {(unit.badges || []).slice(0, 3).map((b) => {
              const sev = worstBadgeSeverity([b]);
              return (
                <span
                  key={b}
                  className={`rounded-full border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide ${
                    sev === 'fault' ? 'border-red-200 bg-[#FFF1F3] text-[#9F1239]'
                      : sev === 'watch' ? 'border-amber-200 bg-amber-50 text-amber-700'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}
                >{unitBadgeLabel(b)}</span>
              );
            })}
            {(unit.badges || []).length > 3 && (
              <span className="text-[9.5px] font-semibold text-stone-400">+{(unit.badges || []).length - 3}</span>
            )}
            {(unit.notes || '').trim() && (
              <span className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-stone-100 px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide text-stone-600">
                <StickyNote size={9} /> Note
              </span>
            )}
          </span>
        </span>
        <span className="text-right font-mono text-[11px] text-stone-500">
          <b className="mb-0.5 block text-[12px] font-bold text-stone-900">{money(savedCost || costCents)}</b>
          {at ? (
            <>
              <b className="block font-semibold text-stone-700">
                {at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </b>
              {at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </>
          ) : '—'}
        </span>
        <ChevronRight size={15} className={`text-stone-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-stone-100 bg-stone-50 p-3">
          {/*
            CAPTIONED, because an uncaptioned barcode reads as "the" number.

            Two different numbers live on this card and they are not
            interchangeable: this one is OURS, minted by us and printed on the
            label we stick on the box, and the serial below is the
            MANUFACTURER's, printed on the device by whoever built it. Drawn
            big and unlabelled, this block looked like the serial, which is
            exactly the confusion to avoid when someone is reconciling a box
            against a vendor's packing list.
          */}
          <div className="mb-3 rounded-lg border border-stone-200 bg-white p-2.5 text-center">
            <div className="mb-1.5 text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
              Internal barcode <span className="font-normal normal-case tracking-normal">· ours, on the label we print</span>
            </div>
            <Barcode value={label} width={320} height={54} />
            <div className="mt-1.5 font-mono text-[11px] tracking-[0.14em] text-stone-700">{label}</div>
          </div>

          {/*
            IDENTITY — what is printed ON the machine, as opposed to the RTA
            label we print FOR it. Two halves, deliberately: the serial is
            typed or scanned because it has to be searchable, and the photo
            keeps everything else the sticker says (FCC ID, WiFi MAC, BT MAC,
            and whatever else the manufacturer chose to print) without anyone
            having to decide in advance which of those will matter.
          */}
          {isSerialized && (
            <div className="mb-3 grid gap-2.5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor={`serial-${unit.unitId}`}
                  className="mb-1.5 block text-[9.5px] font-bold uppercase tracking-wide text-stone-400"
                >
                  Device serial number
                  <span className="ml-1 font-normal normal-case tracking-normal text-stone-400">
                    · the manufacturer&rsquo;s
                  </span>
                </label>
                <input
                  id={`serial-${unit.unitId}`}
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  /* A barcode scanner is a keyboard that ends with Enter. Left
                     to itself that submits the surrounding form and reloads
                     the page mid-receive, so Enter is caught and turned into
                     the save the operator meant. */
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (dirty && !saving) save();
                    }
                  }}
                  placeholder="Scan or type the device's own serial"
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 font-mono text-[13px] text-stone-800 outline-none focus:border-[#DC143C]"
                />
                <p className="mt-1 text-[10.5px] leading-snug text-stone-500">
                  Printed on the device by the manufacturer &mdash; not our barcode above. The one
                  number that has to be searchable; everything else on the sticker lives in the photo.
                </p>
              </div>
              <LabelPhotos
                unit={withPhotos}
                required
                onUnitChanged={(next) => { setLiveUnit(next); onUnitChanged?.(next); }}
              />
              <div className="sm:col-span-2">
                <ExternalBarcodes
                  unit={withPhotos}
                  onUnitChanged={(next) => { setLiveUnit(next); onUnitChanged?.(next); }}
                />
              </div>
            </div>
          )}

          <div className="mb-3 grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-3">
            <Fact label="Received">
              {at ? `${at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—'}
            </Fact>
            <Fact label="State">{unit.state}</Fact>
            <Fact label="Vendor">{vendorName || '—'}</Fact>
            <Fact label="GRN" mono>{grnNumber}</Fact>
            <Fact label="Model">{model?.modelName || unit.modelId}</Fact>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_150px]">
            <div>
              <UnitNotesEditor value={noteValue} onChange={setNoteValue} compact />
            </div>

            <div>
              <span className="mb-1.5 block text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
                Landed cost
              </span>
              <div className="rounded-lg border border-stone-200 bg-white p-2.5">
                <div className="text-right font-mono text-[17px] font-bold text-stone-900">{money(costCents)}</div>
                <dl className="mt-1.5 space-y-0.5 border-t border-stone-100 pt-1.5">
                  {COST_ROWS.map(([k, lbl]) => (
                    <div key={k} className="flex items-baseline justify-between gap-2">
                      <dt className="text-[10.5px] text-stone-500">{lbl}</dt>
                      <dd className="font-mono text-[10.5px] text-stone-600">{money(breakdown[k])}</dd>
                    </div>
                  ))}
                </dl>
                <button
                  type="button"
                  onClick={() => { setDraft(breakdown); setCostOpen(true); }}
                  className="mt-2 inline-flex w-full min-h-[36px] items-center justify-center gap-1.5 rounded-lg border border-stone-300 bg-white text-[12px] font-semibold text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C]"
                >
                  <Pencil size={12} /> Edit cost
                </button>
              </div>
              {/* Cost is per UNIT, not per line: the same model sourced from a
                  second supplier, or a month later, genuinely costs something
                  else, and averaging that away loses the margin on the sale. */}
              <p className="mt-1 text-[10.5px] leading-snug text-stone-500">
                Starts from the device's own economics. Edit it when this one was sourced differently.
              </p>
            </div>
          </div>

          {error && <p className="mt-2 text-[11.5px] font-medium text-[#DC143C]">{error}</p>}

          {/* COST EDITOR — deliberately the SAME shape as the device's own Cost
              card: the three editable lines, then the computed landed figure.
              An operator who maintains costs on the model should not have to
              learn a second way of expressing one here. */}
          {costOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setCostOpen(false)}
            >
              <div
                className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Edit cost for ${label}`}
              >
                <div className="flex items-center justify-between bg-black px-4 py-3 text-white">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-400">Cost</div>
                    <h4 className="text-sm font-bold">{model?.modelName || unit.modelId}</h4>
                    <div className="mt-0.5 font-mono text-[10.5px] text-stone-400">{label}</div>
                  </div>
                  <button onClick={() => setCostOpen(false)} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="h-[3px] bg-gradient-to-r from-[#DC143C] to-[#9F1239]" />

                <div className="p-4">
                  <h5 className="mb-3 text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Economics · what it costs us
                  </h5>
                  {COST_ROWS.map(([k, lbl]) => (
                    <div key={k} className="mb-2 flex items-center justify-between gap-3">
                      <label htmlFor={`${k}-${unit.unitId}`} className="text-[13px] text-stone-600">{lbl}</label>
                      <div className="flex items-center gap-1">
                        <span className="text-[13px] text-stone-400">$</span>
                        <input
                          id={`${k}-${unit.unitId}`}
                          type="number" step="0.01" min="0"
                          value={centsToInput(draft[k])}
                          onChange={(e) => setDraft((d) => ({ ...d, [k]: inputToCents(e.target.value) }))}
                          className="w-28 rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-right font-mono text-[13px] text-stone-800 outline-none focus:border-[#DC143C]"
                        />
                      </div>
                    </div>
                  ))}

                  <div className="mt-3 flex items-center justify-between border-t border-stone-200 pt-3">
                    <span className="text-[13px] font-bold text-stone-900">Landed cost</span>
                    <span className="font-mono text-base font-bold text-stone-900">
                      {money(landedCostCents({ ...draft, defaultOutboundShippingCents: 0 }))}
                    </span>
                  </div>
                  <p className="text-[11px] text-stone-500">Computed — not editable.</p>
                  {/* Outbound shipping is absent on purpose: it is per SHIPMENT,
                      not per unit, which is why landedCostCents() excludes it. */}
                </div>

                <div className="flex justify-end gap-2 border-t border-stone-200 px-4 py-3">
                  <button
                    onClick={() => setCostOpen(false)}
                    className="min-h-[40px] rounded-lg border border-stone-300 px-3.5 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
                  >Cancel</button>
                  <button
                    onClick={() => { setBreakdown(draft); setCostOpen(false); }}
                    className="min-h-[40px] rounded-lg px-3.5 text-[12.5px] font-semibold text-white"
                    style={{ background: '#DC143C' }}
                  >Apply</button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-200 pt-3">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-40"
              style={{ background: '#DC143C' }}
            >
              {saving ? <RubyGemMark size={13} drawing color="currentColor" /> : null}
              {dirty ? 'Save changes' : 'Saved'}
            </button>
            {saved && (
              <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700">
                <Check size={12} /> Saved
              </span>
            )}
            <button
              type="button"
              onClick={() => onPrintLabel(unit.unitId)}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
            >
              <Printer size={13} /> Print label
            </button>
            {onInspectModel && (
              <button
                type="button"
                onClick={() => onInspectModel(unit.modelId)}
                className="min-h-[40px] rounded-lg border border-stone-300 bg-white px-3 text-[12.5px] font-semibold text-stone-700 hover:bg-stone-50"
              >
                Device details
              </button>
            )}
            <span className="ml-auto font-mono text-[11.5px] text-stone-500">
              {money(costCents)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedUnitCard;
